/**
 * Chat Context - Shared state for chat module
 *
 * Manages: current channel, channel tree, unread counts, online users, typing indicators.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useTenant } from './TenantContext';
import { useChatWebSocket } from '../hooks/useChatWebSocket';
import { chatChannelsApi, chatMessagesApi } from '../services/chatApi';
import type {
  Channel,
  ChannelTreeNode,
  ChatMessage,
  TypingUser,
  UserPresence,
  UnreadCount,
} from '../types/chat';

interface ChatContextType {
  // Channel state
  channels: Channel[];
  channelTree: ChannelTreeNode[];
  currentChannel: Channel | null;
  setCurrentChannel: (channel: Channel | null) => void;
  refreshChannels: () => Promise<void>;

  // Messages
  messages: ChatMessage[];
  loadMessages: (channelId: string, beforeId?: string) => Promise<ChatMessage[]>;
  hasMoreMessages: boolean;

  // Unread
  unreadCounts: Map<string, UnreadCount>;

  // Presence & typing
  onlineUsers: Map<number, UserPresence>;
  typingUsers: Map<string, TypingUser[]>;

  // WebSocket
  isConnected: boolean;
  sendTypingStart: (channelId: string) => void;
  sendTypingStop: (channelId: string) => void;
  wsMarkRead: (channelId: string, lastMessageId: string) => void;

  // Add message to local state (optimistic update after send)
  addMessage: (message: ChatMessage) => void;

  // Thread
  threadParentMessage: ChatMessage | null;
  setThreadParentMessage: (msg: ChatMessage | null) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

function buildTree(channels: Channel[]): ChannelTreeNode[] {
  const map = new Map<string, ChannelTreeNode>();
  const roots: ChannelTreeNode[] = [];

  for (const ch of channels) {
    map.set(ch.id, { ...ch, children: [] });
  }

  for (const ch of channels) {
    const node = map.get(ch.id)!;
    if (ch.parent_id && map.has(ch.parent_id)) {
      map.get(ch.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  }

  return roots;
}

export const ChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const tenantId = tenant?.id || '';

  const [channels, setChannels] = useState<Channel[]>([]);
  const [currentChannel, setCurrentChannel] = useState<Channel | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState<Map<string, UnreadCount>>(new Map());
  const [onlineUsers, setOnlineUsers] = useState<Map<number, UserPresence>>(new Map());
  const [typingUsers, setTypingUsers] = useState<Map<string, TypingUser[]>>(new Map());
  const [threadParentMessage, setThreadParentMessage] = useState<ChatMessage | null>(null);

  // Add a message to local state, deduplicating by id
  const addMessage = useCallback((message: ChatMessage) => {
    setMessages(prev => {
      if (prev.some(m => m.id === message.id)) return prev;
      return [message, ...prev];
    });
  }, []);

  // WebSocket callbacks
  const handleNewMessage = useCallback((message: ChatMessage) => {
    if (currentChannel && message.channel_id === currentChannel.id) {
      addMessage(message);
    }
  }, [currentChannel, addMessage]);

  const handleMessageUpdated = useCallback((data: { id: string; content: string; is_edited: boolean }) => {
    setMessages(prev => prev.map(m =>
      m.id === data.id ? { ...m, content: data.content, is_edited: data.is_edited } : m
    ));
  }, []);

  const handleMessageDeleted = useCallback((data: { id: string; channel_id: string }) => {
    setMessages(prev => prev.map(m =>
      m.id === data.id ? { ...m, is_deleted: true, content: '[deleted]' } : m
    ));
  }, []);

  const handleTyping = useCallback((data: TypingUser) => {
    setTypingUsers(prev => {
      const next = new Map(prev);
      const channelTyping = (next.get(data.channel_id) || []).filter(t => t.user_id !== data.user_id);
      if (data.is_typing) {
        channelTyping.push(data);
      }
      next.set(data.channel_id, channelTyping);
      return next;
    });
  }, []);

  const handlePresence = useCallback((data: UserPresence) => {
    setOnlineUsers(prev => {
      const next = new Map(prev);
      next.set(data.user_id, data);
      return next;
    });
  }, []);

  const handleReaction = useCallback((data: { message_id: string; emoji: string; user_id: number; action: string }) => {
    setMessages(prev => prev.map(m => {
      if (m.id !== data.message_id) return m;
      const reactions = [...(m.reactions || [])];
      const existing = reactions.find(r => r.emoji === data.emoji);
      if (data.action === 'added') {
        if (existing) {
          existing.count += 1;
          existing.user_ids.push(data.user_id);
        } else {
          reactions.push({ emoji: data.emoji, count: 1, user_ids: [data.user_id] });
        }
      } else {
        if (existing) {
          existing.count = Math.max(0, existing.count - 1);
          existing.user_ids = existing.user_ids.filter(id => id !== data.user_id);
        }
      }
      return { ...m, reactions: reactions.filter(r => r.count > 0) };
    }));
  }, []);

  const handleUnreadUpdate = useCallback((data: { channel_id: string; unread_count: number; mention_count: number }) => {
    setUnreadCounts(prev => {
      const next = new Map(prev);
      next.set(data.channel_id, data);
      return next;
    });
  }, []);

  const { isConnected, sendTypingStart, sendTypingStop, markRead: wsMarkRead, subscribeChannels } =
    useChatWebSocket({
      tenantId: tenantId || null,
      onNewMessage: handleNewMessage,
      onMessageUpdated: handleMessageUpdated,
      onMessageDeleted: handleMessageDeleted,
      onTyping: handleTyping,
      onPresence: handlePresence,
      onReaction: handleReaction,
      onUnreadUpdate: handleUnreadUpdate,
    });

  // Load channels
  const refreshChannels = useCallback(async () => {
    try {
      const data = await chatChannelsApi.list();
      setChannels(data);
      // Subscribe to channels via WebSocket
      subscribeChannels(data.map(c => c.id));
    } catch (e) {
      console.error('Failed to load channels:', e);
    }
  }, [subscribeChannels]);

  // Load unread counts
  const refreshUnread = useCallback(async () => {
    try {
      const data = await chatMessagesApi.getUnreadCounts();
      const map = new Map<string, UnreadCount>();
      for (const item of data) {
        map.set(item.channel_id, item);
      }
      setUnreadCounts(map);
    } catch (e) {
      console.error('Failed to load unread counts:', e);
    }
  }, []);

  // Load messages for a channel
  const loadMessages = useCallback(async (channelId: string, beforeId?: string): Promise<ChatMessage[]> => {
    try {
      const data = await chatMessagesApi.list(channelId, beforeId, 50);
      if (!beforeId) {
        setMessages(data);
      } else {
        setMessages(prev => [...prev, ...data]);
      }
      setHasMoreMessages(data.length === 50);
      return data;
    } catch (e) {
      console.error('Failed to load messages:', e);
      return [];
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (user && tenantId) {
      refreshChannels();
      refreshUnread();
    }
  }, [user, tenantId, refreshChannels, refreshUnread]);

  // Load messages when channel changes
  useEffect(() => {
    if (currentChannel) {
      setMessages([]);
      setHasMoreMessages(true);
      loadMessages(currentChannel.id);
      setThreadParentMessage(null);
    }
  }, [currentChannel, loadMessages]);

  const channelTree = buildTree(channels);

  const value: ChatContextType = {
    channels,
    channelTree,
    currentChannel,
    setCurrentChannel,
    refreshChannels,
    messages,
    loadMessages,
    hasMoreMessages,
    unreadCounts,
    onlineUsers,
    typingUsers,
    isConnected,
    sendTypingStart,
    sendTypingStop,
    wsMarkRead,
    addMessage,
    threadParentMessage,
    setThreadParentMessage,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export function useChat(): ChatContextType {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
}
